<script setup lang="ts">
import type { TodosSubmissionsStatus } from "@profile-service-types/index.ts";
import { computed, ref, toRef } from "vue";
import { useToast } from "vue-toast-notification";
import { useUploadOnBehalfAvailability } from "@/components/MyStudents/Submissions/composables/useUploadOnBehalfAvailability";
import { useUploadSubmissionOnBehalf } from "@/components/MyStudents/Submissions/composables/useUploadSubmissionOnBehalf";
import type { UploadRules } from "@/components/MyStudents/Submissions/types";
import { getUploadRules } from "@/components/MyStudents/Submissions/utils";
import UploadArrowIcon from "@/components/shared/icons/UploadArrowIcon.vue";
import { ERROR_TIMEOUT } from "@/constants.js";
import UploadOnBehalfModal from "./components/UploadOnBehalfModal.vue";
import { uploadOnBehalfTooltipPt } from "./styles";
import type { UploadOnBehalfConfirmPayload, UploadOnBehalfProps } from "./types";

const props = withDefaults(defineProps<UploadOnBehalfProps>(), {
  participantId: undefined,
  participantName: undefined,
  participantFirstName: undefined,
  participantLastName: undefined,
  orderId: undefined,
});

const toast = useToast();
const { isVisible, isEnabled, tooltipMessage } = useUploadOnBehalfAvailability(toRef(props, "todoSubmission"));
const { mutateAsync: uploadSubmission, isPending: isSubmitting } = useUploadSubmissionOnBehalf();

const isModalOpen = ref<boolean>(false);

const uploadRules = computed<UploadRules>(() => getUploadRules(props.todoSubmission?.permitted_media_types));
const permittedStatuses = computed<TodosSubmissionsStatus[]>(() => props.todoSubmission?.permitted_statuses ?? []);

const tooltipConfig = computed(() => ({
  value: tooltipMessage.value,
  disabled: !tooltipMessage.value,
  escape: true,
  class: "!shadow-none",
  hideDelay: "0.3",
  pt: uploadOnBehalfTooltipPt,
}));

const openModal = (): void => {
  if (isEnabled.value) {
    isModalOpen.value = true;
  }
};

const closeModal = (): void => {
  if (!isSubmitting.value) {
    isModalOpen.value = false;
  }
};

const onConfirm = async ({ file, status }: UploadOnBehalfConfirmPayload): Promise<void> => {
  try {
    await uploadSubmission({
      file,
      status,
      applicationId: props.applicationId,
      participantId: props.participantId ?? "",
      orderId: props.orderId,
      todoId: props.todoId,
      todoTitle: props.todoTitle,
      studentFirstName: props.participantFirstName,
      studentLastName: props.participantLastName,
    });
  } catch {
    toast.open({
      message: "Error while uploading the submission. Please, try it later.",
      type: "error",
      position: "bottom",
      duration: ERROR_TIMEOUT,
    });
  } finally {
    isModalOpen.value = false;
  }
};
</script>

<template>
  <template v-if="isVisible">
    <span v-tooltip.bottom="tooltipConfig" class="inline-flex shrink-0" data-testid="upload-on-behalf-button-wrapper">
      <button
        type="button"
        class="inline-flex items-center gap-1.5 h-8 px-3 rounded border border-teal-900 bg-teal-900 text-13p font-bold tracking-[0.4px] text-white transition-colors duration-150 hover:bg-[#006667] disabled:cursor-not-allowed disabled:bg-gray-175 disabled:text-gray-600 disabled:hover:bg-gray-175"
        :disabled="!isEnabled"
        data-testid="upload-on-behalf-button"
        @click="openModal"
      >
        <UploadArrowIcon class="w-3.5 h-3.5" />
        Upload file
      </button>
    </span>
    <UploadOnBehalfModal
      :visible="isModalOpen"
      :todo-title="todoTitle"
      :participant-name="participantName"
      :upload-rules="uploadRules"
      :permitted-statuses="permittedStatuses"
      :is-submitting="isSubmitting"
      @cancel="closeModal"
      @confirm="onConfirm"
    />
  </template>
</template>
